import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";

import CredibilityDiscussionFeed from "../DashboardComponents/CredibilityDiscussionFeed";
import TrendingConversationsPanel from "../DashboardComponents/TrendingConversations";
import FollowedJournalistsPanel from "./FollowedJournalistsPanel";
import SocialDiscoveryEmptyState from "./SocialDiscoveryEmptyState";
import MarketActivityPanel from "../DashboardComponents/StockerSignalPanel";
import { fetchFollowedJournalistsThunk } from "../../../redux/news/newsActionThunks";
import { selectFollowedJournalists } from "../../../redux/news/newsSlice";

// Optional: you can add these later if you want the wrapper to expand further
// import ContextActivityRadar from "../DashboardComponents/StockerActivity";
// import TechnicalContextPanel from "../DashboardComponents/TechnicalContext";

const ConsolidatedSocialSection = ({
  posts = [],
  trendingConversations = [],
  groups = [],
  activeStock = null,
  feedFilter = "watchlist",
  onFeedFilterChange,
  trendingTab = "credible",
  setTrendingTab,
  signal = null,
  signalLoading = false,
  signalError = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const journalists = useSelector(selectFollowedJournalists);
  const journalistsLoading = useSelector((s) => s.news?.followedJournalistsLoading);

  useEffect(() => {
    if (journalists.length === 0) dispatch(fetchFollowedJournalistsThunk());
  }, [dispatch, journalists.length]);

  // Both Trending Conversations and Followed Journalists are empty for
  // most accounts today -- showing each one's full "icon + heading + CTA"
  // empty state stacked on top of the other reads as a wall of near-
  // identical placeholder cards (see 2026-08-30 layout review). Fold them
  // into one compact card instead once we know both are genuinely empty
  // (not just still loading).
  const showConsolidatedEmptyState =
    trendingConversations.length === 0 && !journalistsLoading && journalists.length === 0;

  // Build a watchlist ticker set from trending + groups + posts if needed.
  // If your TrendingConversationsPanel expects a watchlist set for badge logic,
  // this prevents undefined access.
  const watchlistTickersSet = useMemo(() => {
    const s = new Set();
    trendingConversations.forEach(t => t?.ticker && s.add(t.ticker));
    groups.forEach(g => (g?.tickers || []).forEach(t => s.add(t)));
    posts.forEach(p => (p?.tickers || []).forEach(t => s.add(t)));
    return s;
  }, [trendingConversations, groups, posts]);

  return (
    <section className="mt-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* LEFT: Main credibility-weighted discussion feed */}
        <div className="lg:col-span-2">
          <CredibilityDiscussionFeed
            posts={posts}
            activeStock={activeStock}
            feedFilter={feedFilter}
            onFeedFilterChange={onFeedFilterChange}
          />
        </div>

        {/* RIGHT: Trending + things you follow (Groups, Journalists) */}
        <div className="flex flex-col gap-4">
          {/* RelevantGroups (Recommended Groups) used to render here too --
              removed 2026-08-30. GroupsQuickAccess already promotes the same
              relevantGroups data to the top of the dashboard, so this was a
              literal duplicate card with identical content further down the
              page (caught via a live screenshot showing "Recommended Groups"
              twice). Its activeStock ticker-filter wasn't even wired here
              (onActiveStockChange was never passed), so nothing functional
              is lost. */}

          {showConsolidatedEmptyState ? (
            <SocialDiscoveryEmptyState />
          ) : (
            <>
              <TrendingConversationsPanel
                trendingConversations={trendingConversations}
                trendingTab={trendingTab}
                setTrendingTab={setTrendingTab}
                watchlistTickersSet={watchlistTickersSet}
              />
              <FollowedJournalistsPanel journalists={journalists} loading={journalistsLoading} />
            </>
          )}
        </div>
      </div>

      {/* Per-stock engagement signal -- only meaningful once a stock is
          selected/expanded, since `signal` is fetched per-ticker. */}
      {activeStock && (
        <div className="mt-4">
          <MarketActivityPanel signal={signal} loading={signalLoading} error={signalError} />
        </div>
      )}

      {/* Single shared disclaimer for this whole section (2026-07-17
          cleanup) -- previously repeated 6+ times near-identically across
          CredibilityDiscussionFeed (x2) and TrendingConversationsPanel (x2).
          MarketActivityPanel (StockerSignalPanel.jsx) keeps its own --
          that component is also rendered independently elsewhere on the
          dashboard (outside this section), so its disclaimer can't be
          removed without regressing that other usage. */}
      <div className="mt-4 px-4 py-3 rounded-lg bg-cream-100 dark:bg-slate-900/40 border border-cream-300 dark:border-slate-800/40">
        <p className="text-[11px] text-cream-700 dark:text-slate-500 text-center leading-relaxed flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3 h-3 flex-shrink-0" />
          {t('dashboard.socialSection.disclaimer')}
        </p>
      </div>

      {/*
        If you later want "context" in this wrapper too, you can add:
        <ContextActivityRadar events={...} activeStock={activeStock} />
        <TechnicalContextPanel ... />
        (Deferred deliberately -- see UnifiedStockerDashboard.jsx's top-of-file
        note: contextEvents already render per-card in ExpandableStockCard's
        "Context" tab, a better UX location.)
      */}
    </section>
  );
};

export default ConsolidatedSocialSection;
